import { useCallback, useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/shared/components/ui/input-otp';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import type {
  SendPasswordRecoveryCodeResponseDto,
  VerifyPasswordRecoveryCodeRequestDto,
} from '@/features/auth-login';
import {
  AuthCard,
  usePasswordRecoveryConfirmation,
  usePasswordRecoveryConfirmForm,
  verifyPasswordRecoveryCode,
} from '@/features/auth-login';
import {
  DEFAULT_OTP_CODE_LENGTH,
  DEFAULT_OTP_TIMEOUT_IN_MS,
} from '@/shared/config';
import { useTimer } from '@/shared/lib';
import { useMutation } from '@tanstack/react-query';

export type LoginPasswordRecoveryConfirmLocationState = {
  email: string;
  state: string;
  codeData?: SendPasswordRecoveryCodeResponseDto;
};

const LoginPasswordRecoveryConfirmForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  if (!location.state || !location.state.email || !location.state.state) {
    return <Navigate to="/login/password-recovery" />;
  }

  const { email, codeData } =
    location.state as LoginPasswordRecoveryConfirmLocationState;

  const { timerValue, startTimer, isTimerRunning } = useTimer();

  const { form, setServerError, clearServerError } =
    usePasswordRecoveryConfirmForm({
      email,
    });

  const {
    sendRecoveryCode,
    isSubmitting: isResendSubmitting,
    error: resendError,
  } = usePasswordRecoveryConfirmation({
    onSuccess: data => {
      if (data.success) {
        clearServerError();
        startTimer(Math.floor(DEFAULT_OTP_TIMEOUT_IN_MS / 1000));
      }
    },
  });

  const handlePasswordRecoveryVerificationResponse = async (
    args: VerifyPasswordRecoveryCodeRequestDto
  ) => {
    const response = await verifyPasswordRecoveryCode(args);

    if (response.status === 400 && response.data?.code) {
      const errorCode = response.data.code;
      if (errorCode === 'PASSWORD_REQUIRED') {
        return { success: true, requiresPasswordReset: true };
      } else if (errorCode === 'INCORRECT_CODE') {
        throw new Error('INCORRECT_CODE');
      }
    }

    if (response.status === 400) {
      throw new Error('INCORRECT_CODE');
    }

    if (
      response.status === 429 ||
      response.data?.code === 'TOO_MANY_REQUESTS'
    ) {
      throw new Error(response.data?.message || 'TOO_MANY_REQUESTS');
    }

    if (response.status !== 200) {
      throw new Error('VERIFICATION_FAILED');
    }

    return { success: true };
  };

  const verifyMutation = useMutation({
    mutationFn: handlePasswordRecoveryVerificationResponse,
    onSuccess: data => {
      clearServerError();
      if (data.requiresPasswordReset) {
        const enteredCode = form.getValues('code');
        navigate('/login/set-new-password', {
          state: { email, code: enteredCode },
          replace: true,
        });
      }
    },
    onError: (error: Error) => {
      if (error.message === 'INCORRECT_CODE') {
        setServerError(
          t('authPages.passwordRecoveryConfirmationForm.incorrectCodeError')
        );
      } else if (error.message === 'TOO_MANY_REQUESTS') {
        setServerError(
          t('authPages.passwordRecoveryConfirmationForm.tooManyRequestsError')
        );
      } else if (error.message === 'VERIFICATION_FAILED') {
        setServerError(
          t(
            'authPages.passwordRecoveryConfirmationForm.verificationFailedError'
          )
        );
      } else {
        setServerError(error.message || t('common.unknownError'));
      }
    },
  });

  useEffect(() => {
    startTimer(Math.floor(DEFAULT_OTP_TIMEOUT_IN_MS / 1000));
  }, [codeData]);

  const resendCode = useCallback(async () => {
    try {
      await sendRecoveryCode(email);
    } catch (error) {
      console.error('Failed to resend code:', error);
    }
  }, [email, sendRecoveryCode]);

  const onSubmit = async (data: VerifyPasswordRecoveryCodeRequestDto) => {
    try {
      await verifyMutation.mutateAsync(data);
    } catch (error) {
      console.error('Failed to verify code:', error);
    }
  };

  const handleCodeChange = (value: string) => {
    form.setValue('code', value);
    clearServerError();

    if (value.length === codeLength) {
      form.trigger('code').then(isValid => {
        if (isValid) {
          form.handleSubmit(onSubmit)();
        }
      });
    }
  };

  const codeLength = DEFAULT_OTP_CODE_LENGTH;

  return (
    <AuthCard
      title={t('authPages.passwordRecoveryConfirmationForm.title')}
      description={`${t('authPages.passwordRecoveryConfirmationForm.description')} ${email}`}
      backButton={{ link: '/login/password-recovery', state: location.state }}>
      <CardContent className="p-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem className="mb-[1.4rem]">
                  <FormControl>
                    <InputOTP
                      maxLength={codeLength}
                      pattern={REGEXP_ONLY_DIGITS}
                      value={field.value}
                      onChange={handleCodeChange}
                      disabled={verifyMutation.isPending}>
                      <InputOTPGroup className="w-full">
                        {Array.from({ length: codeLength }).map((_, index) => (
                          <InputOTPSlot
                            key={index}
                            index={index}
                            isError={!!form.formState.errors.code}
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="button"
              variant="link"
              onClick={resendCode}
              disabled={isTimerRunning || isResendSubmitting}
              className="headline5 p-0">
              {isTimerRunning
                ? `${t('authPages.passwordRecoveryConfirmationForm.resendCodePending')} ${timerValue}`
                : t(
                    'authPages.passwordRecoveryConfirmationForm.resendCodeButton'
                  )}
            </Button>

            {resendError && (
              <p className="text-destructive p3 py-[1.2rem]">
                {(resendError as Error).message || t('common.unknownError')}
              </p>
            )}
          </form>
        </Form>
      </CardContent>
    </AuthCard>
  );
};

export { LoginPasswordRecoveryConfirmForm };
